import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";

// Fallback nav until categories load (matches the redesign brief)
const FALLBACK_NAV = [
  "Shqipëri",
  "Kosovë",
  "Maqedoni",
  "Botë",
  "Ekonomi",
  "Sport",
  "Kulturë",
  "Rozë",
  "Kronikë",
];

export default function Header() {
  const [categories, setCategories] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/categories`)
      .then((res) => setCategories(res.data || []))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const nav = categories.length > 0 ? categories : FALLBACK_NAV;
  const currentCategory = decodeURIComponent(
    (location.pathname.split("/")[2] || "").trim()
  );

  return (
    <header className="sticky top-0 z-40 border-b border-line bg-surface/95 backdrop-blur">
      <div className="mx-auto flex max-w-site items-center gap-4 px-4 py-3 lg:gap-8 lg:px-10 lg:py-4">
        {/* Mobile hamburger */}
        <button
          type="button"
          className="grid h-9 w-9 place-items-center rounded-pill text-ink hover:bg-chip lg:hidden"
          aria-label="Menu"
          onClick={() => setMenuOpen((v) => !v)}
        >
          <span className="text-xl leading-none">☰</span>
        </button>

        {/* Logo */}
        <Link to="/" className="shrink-0">
          <img
            src="/logo-horizontal.svg"
            alt="lajm.ai"
            className="h-[26px] w-auto lg:h-[30px]"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden flex-1 items-center gap-5 overflow-x-auto lg:flex">
          {nav.map((item) => {
            const isActive = currentCategory === item;
            return (
              <Link
                key={item}
                to={`/category/${item}`}
                className={`whitespace-nowrap font-head text-[14.5px] font-semibold transition-colors hover:text-ink ${
                  isActive ? "text-brand" : "text-sub"
                }`}
              >
                {item}
              </Link>
            );
          })}
          <Link
            to="/impressum"
            className="whitespace-nowrap font-head text-[14.5px] font-semibold text-sub hover:text-ink"
          >
            Rreth nesh
          </Link>
        </nav>

        {/* Search — full pill on desktop, icon-only on mobile (right edge of header) */}
        <button
          type="button"
          onClick={() => navigate("/search")}
          aria-label="Kërko"
          className="ml-auto hidden items-center gap-2 rounded-pill border border-line bg-bg px-4 py-2.5 text-[13.5px] text-sub transition hover:border-ai hover:text-ai md:flex"
        >
          <span aria-hidden>⌕</span>
          {/* TODO: restore "Kërko ose pyet AI…" once Pyet AI is wired up */}
          Kërko…
        </button>
        <button
          type="button"
          onClick={() => navigate("/search")}
          aria-label="Kërko"
          className="ml-auto grid h-9 w-9 place-items-center rounded-pill text-ink hover:bg-chip md:hidden"
        >
          <span aria-hidden className="text-lg leading-none">⌕</span>
        </button>

        {/* Social on desktop */}
        <div className="hidden items-center gap-2 lg:flex">
          <a
            href="https://www.facebook.com/LajmeAI/"
            target="_blank"
            rel="noopener noreferrer"
            className="grid h-9 w-9 place-items-center rounded-pill text-sub hover:bg-chip hover:text-brand"
            title="Facebook"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
          </a>
          <a
            href="https://www.instagram.com/lajm.ai/"
            target="_blank"
            rel="noopener noreferrer"
            className="grid h-9 w-9 place-items-center rounded-pill text-sub hover:bg-chip hover:text-brand"
            title="Instagram"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
            </svg>
          </a>
        </div>
      </div>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="border-t border-line bg-surface lg:hidden">
          <nav className="mx-auto flex max-w-site flex-col gap-1 px-4 py-3">
            {nav.map((item) => (
              <Link
                key={item}
                to={`/category/${item}`}
                onClick={() => setMenuOpen(false)}
                className="rounded-card px-3 py-2 font-head text-[15px] font-semibold text-ink hover:bg-chip"
              >
                {item}
              </Link>
            ))}
            <Link
              to="/impressum"
              onClick={() => setMenuOpen(false)}
              className="rounded-card px-3 py-2 font-head text-[15px] font-semibold text-sub hover:bg-chip"
            >
              Rreth nesh
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
